<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">登記候位</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">處理中...</span>
          <span v-else>登記候位</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-waitlist-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">候位資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="storeId" class="required">門市</label>
              <select
                id="storeId"
                v-model="form.storeId"
                class="form-control"
                :class="{ 'is-invalid': errors.storeId }"
                required
                :disabled="!!route.query.storeId"
              >
                <option value="">請選擇門市</option>
                <option v-for="store in stores" :key="store.id" :value="store.id">
                  {{ store.storeName }}
                </option>
              </select>
              <div v-if="errors.storeId" class="invalid-feedback">
                {{ errors.storeId }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="guestName" class="required">顧客姓名</label>
              <input
                id="guestName"
                v-model="form.guestName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.guestName }"
                placeholder="請輸入顧客姓名"
                required
                maxlength="100"
              />
              <div v-if="errors.guestName" class="invalid-feedback">
                {{ errors.guestName }}
              </div>
            </div>

            <div class="form-group">
              <label for="guestPhone" class="required">聯絡電話</label>
              <input
                id="guestPhone"
                v-model="form.guestPhone"
                type="tel"
                class="form-control"
                :class="{ 'is-invalid': errors.guestPhone }"
                placeholder="請輸入聯絡電話"
                required
                maxlength="20"
              />
              <div v-if="errors.guestPhone" class="invalid-feedback">
                {{ errors.guestPhone }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="guestCount" class="required">用餐人數</label>
              <input
                id="guestCount"
                v-model.number="form.guestCount"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.guestCount }"
                min="1"
                required
              />
              <div v-if="errors.guestCount" class="invalid-feedback">
                {{ errors.guestCount }}
              </div>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'CreateWaitlist',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const stores = ref([]);
    const submitting = ref(false);
    const errors = ref({});

    const form = ref({
      storeId: route.query.storeId || '',
      guestName: '',
      guestPhone: '',
      guestCount: 2
    });

    const fetchStores = async () => {
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 100 });
        if (response.success && response.data?.items) {
          stores.value = response.data.items;
          // 優先使用 URL 的 storeId，其次預設第一家門市
          if (route.query.storeId) {
            form.value.storeId = route.query.storeId;
          } else if (!form.value.storeId && stores.value.length > 0) {
            form.value.storeId = stores.value[0].id;
          }
        }
      } catch (err) {
        console.error('Failed to fetch stores:', err);
      }
    };

    const handleSubmit = async () => {
      errors.value = {};
      submitting.value = true;

      try {
        await reservationService.createWaitlist(form.value.storeId, {
          guestName: form.value.guestName,
          guestPhone: form.value.guestPhone,
          guestCount: form.value.guestCount
        });
        showSuccess('候位登記成功');
        router.push('/client/waitlist');
      } catch (err) {
        const errorData = err.response?.data;
        if (errorData?.error?.details) {
          errors.value = errorData.error.details;
        }
        showError(errorData?.message || '登記候位失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/waitlist');
    };

    onMounted(() => {
      fetchStores();
    });

    return {
      route,
      form,
      stores,
      submitting,
      errors,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.create-waitlist-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
